import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../api';

// Async thunks
export const getPartners = createAsyncThunk(
  'partners/getPartners',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.fetchPartners();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error fetching partners');
    }
  }
);

export const getPartner = createAsyncThunk(
  'partners/getPartner',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.fetchPartner(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error fetching partner');
    }
  }
);

export const createPartner = createAsyncThunk(
  'partners/createPartner',
  async ({ partnerData }, { rejectWithValue }) => {
    try {
      const { data } = await api.createPartner(partnerData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add partner');
    }
  }
);

export const updatePartner = createAsyncThunk(
  'partners/updatePartner',
  async ({ id, updatedPartnerData }, { rejectWithValue }) => {
    try {
      const { data } = await api.updatePartner({ id, partnerData: updatedPartnerData });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update partner');
    }
  }
);

export const deletePartner = createAsyncThunk(
  'partners/deletePartner',
  async ({ id }, { rejectWithValue }) => {
    try {
      await api.deletePartner(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete partner');
    }
  }
);

const partnerSlice = createSlice({
  name: 'partner',
  initialState: {
    partners: [],
    partner: null,
    loading: false,
    error: null,
  },
  reducers: {
    setCurrentPartner: (state, action) => {
      state.partner = action.payload;
    },
    clearCurrentPartner: (state) => {
      state.partner = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get partners
      .addCase(getPartners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPartners.fulfilled, (state, action) => {
        state.loading = false;
        state.partners = action.payload;
        state.error = null;
      })
      .addCase(getPartners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get single partner
      .addCase(getPartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPartner.fulfilled, (state, action) => {
        state.loading = false;
        state.partner = action.payload;
        state.error = null;
      })
      .addCase(getPartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create partner
      .addCase(createPartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPartner.fulfilled, (state, action) => {
        state.loading = false;
        state.partners.push(action.payload);
        state.error = null;
      })
      .addCase(createPartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update partner
      .addCase(updatePartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePartner.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.partners.findIndex(p => p._id === action.payload._id);
        if (index !== -1) {
          state.partners[index] = action.payload;
        }
        if (state.partner && state.partner._id === action.payload._id) {
          state.partner = action.payload;
        }
        state.error = null;
      })
      .addCase(updatePartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete partner
      .addCase(deletePartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePartner.fulfilled, (state, action) => {
        state.loading = false;
        state.partners = state.partners.filter(p => p._id !== action.payload);
        if (state.partner && state.partner._id === action.payload) {
          state.partner = null;
        }
        state.error = null;
      })
      .addCase(deletePartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentPartner, clearCurrentPartner } = partnerSlice.actions;
export default partnerSlice.reducer;

